import { Api, Attachment } from "@relode/core";

import {
    ChangeJobStatusRequest,
    JobCandidatesFunnelResponse,
    JobDetailsResponse,
    JobEngagementFunnelResponse,
    JobPendingCreateResponse,
    JobPendingStepOneCreateResponse,
    JobPendingStepOneRequest,
    JobPendingStepOneResponse,
    JobPendingStepThreeResponse,
    JobPendingStepTwoRequest,
    JobPendingStepTwoResponse,
    JobSearchRequest,
    JobShareResponse,
    JobStatsResponse,
} from ".";
import {
    SearchResults,
} from "../../../../features/jobs/_infra/search/jobSearchState";
import { JobManagerContactResponse } from "../../shared";
import { TeamMemberRequest, TeamMemberSuggestionResponse } from "../teams";

const function_name = (name: string) => `client.backend.services.jobs.${name}`;

export const jobService = {
    search: async (
        criteria: JobSearchRequest,
        signal?: AbortSignal
    ): Promise<SearchResults> => {
        const url = "/jobs/search";
        const result = await Api.url(url)
            .body({
                ...criteria,
            })
            .retries(3)
            .useSignal(signal)
            .throwOnAbort()
            .track({ name: function_name("search"), data: { criteria } })
            .post<SearchResults>();
        return result;
    },
    uploadAttachment: async (
        attachment: File,
        signal?: AbortSignal
    ): Promise<Attachment> => {
        const url = "/jobs/attachments";
        const attachmentData = new FormData();
        attachmentData.append("attachment", attachment);

        const result = await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("uploadAttachment"),
                data: { attachment: JSON.stringify(attachment) },
            })
            .body(attachmentData)
            .post<Attachment>();
        return result;
    },

    getJobManagerDetails: async (
        jobId: number,
        signal?: AbortSignal
    ): Promise<JobManagerContactResponse> => {
        const url = `/jobs/${jobId}/jobManager`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("getJobManagerDetails") })
            .get<JobManagerContactResponse>();
        return result;
    },

    openJob: async (
        jobId: number,
        request: ChangeJobStatusRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/${jobId}/open`;
        await Api.url(url)
            .body({
                ...request,
            })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("openJob"),
                data: {
                    ...request,
                },
            })
            .put<void>();
    },

    pauseJob: async (
        jobId: number,
        request: ChangeJobStatusRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/${jobId}/pause`;
        await Api.url(url)
            .body({
                ...request,
            })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("pauseJob"),
                data: {
                    ...request,
                },
            })
            .put<void>();
    },

    closeJob: async (
        jobId: number,
        request: ChangeJobStatusRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/${jobId}/close`;
        await Api.url(url)
            .body({
                ...request,
            })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("closeJob"),
                data: {
                    ...request,
                },
            })
            .put<void>();
    },
    getJobDetails: async (
        jobId: number,
        signal?: AbortSignal
    ): Promise<JobDetailsResponse> => {
        const url = `/jobs/${jobId}`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("getJobDetails"), data: { jobId } })
            .get<JobDetailsResponse>();
        return result;
    },
    getTeamMemberSuggestions: async (
        jobId: number,
        searchText: string,
        signal?: AbortSignal
    ): Promise<TeamMemberSuggestionResponse[]> => {
        const url = `/jobs/${jobId}/members/suggest`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("getTeamMemberSuggestions"),
                data: { jobId, searchText },
            })
            .body({ searchText: searchText })
            .post<TeamMemberSuggestionResponse[]>();
        return result;
    },

    getShareLink: async (
        jobId: number,
        signal?: AbortSignal
    ): Promise<JobShareResponse> => {
        const url = `/jobs/${jobId}/share/public`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("getShareLink"), data: { jobId } })
            .post<JobShareResponse>();
        return result;
    },

    addTeamMember: async (
        jobId: number,
        request: TeamMemberRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/${jobId}/members/${request.userId}`;
        await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("addTeamMember"),
                data: { jobId, ...request },
            })
            .body(request.clientJobUserTypeId)
            .post<never>();
    },

    updateTeamMember: async (
        jobId: number,
        request: TeamMemberRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/${jobId}/members/${request.userId}`;
        await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("updateTeamMember"),
                data: { jobId, ...request },
            })
            .body(request.clientJobUserTypeId)
            .put<never>();
    },

    deleteTeamMember: async (
        jobId: number,
        userId: number,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/${jobId}/members/${userId}`;
        await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("deleteTeamMember"),
                data: { jobId, userId },
            })
            .call("DELETE");
    },

    getAddJobNewStep1: async (
        jobId: number,
        signal?: AbortSignal
    ): Promise<JobPendingStepOneResponse | void> => {
        const url = `/jobs/pending/${jobId}/step1`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("getAddJobStep1"), data: { jobId } })
            .get<JobPendingStepOneResponse | void>();
        return result;
    },

    saveAddJobNewStep1: async (
        jobId: number,
        request: JobPendingStepOneRequest,
        signal?: AbortSignal
    ): Promise<JobPendingStepOneCreateResponse> => {
        const url = `/jobs/pending/${jobId}/step1`;
        const result = await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("saveAddJobStep1"),
                data: { jobId, ...request },
            })
            .body({ ...request })
            .put<JobPendingStepOneCreateResponse>();
        return result;
    },

    getAddJobStep2: async (
        jobId: number,
        signal?: AbortSignal
    ): Promise<JobPendingStepTwoResponse> => {
        const url = `/jobs/pending/${jobId}/step2`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("getAddJobStep2"), data: { jobId } })
            .get<JobPendingStepTwoResponse>();
        return result;
    },

    saveAddJobStep2: async (
        jobId: number,
        request: JobPendingStepTwoRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/pending/${jobId}/step2`;
        const result = await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("saveAddJobStep2"),
                data: { jobId, ...request },
            })
            .body({ ...request })
            .post<void>();
        return result;
    },

    getAddJobStep3: async (
        jobId: number,
        signal?: AbortSignal
    ): Promise<JobPendingStepThreeResponse> => {
        const url = `/jobs/pending/${jobId}/step3`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("getAddJobStep3"), data: { jobId } })
            .get<JobPendingStepThreeResponse>();
        return result;
    },

    moveJobWithoutPlanToRequested: async (
        jobId: number,
        request: JobPendingStepTwoRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/pending/${jobId}/step2`;
        const result = await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("moveJobWithoutPlanToRequested"),
                data: { jobId, ...request },
            })
            .body({ ...request })
            .put<void>();
        return result;
    },

    moveJobWithPlanToRequested: async (
        jobId: number,
        jobConcurrencyToken: string,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/jobs/pending/${jobId}/step3`;
        const result = await Api.url(url)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("moveJobWithPlanToRequested"),
                data: { jobId, jobConcurrencyToken },
            })
            .body({ jobConcurrencyToken })
            .put<void>();
        return result;
    },

    copyJob: async (
        jobId: number,
        signal?: AbortSignal
    ): Promise<JobPendingCreateResponse> => {
        const url = `/jobs/pending/${jobId}/copy`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("copyJob"), data: { jobId } })
            .post<JobPendingCreateResponse>();
        return result;
    },

    loadJobCandidatesFunnel: async (
        jobId: number
    ): Promise<JobCandidatesFunnelResponse> => {
        const url = `/jobs/${jobId}/candidates/funnel`;
        const result = await Api.url(url)
            .throwOnAbort(true)
            .retries(3)
            .useSignal()
            .track({
                name: "loadJobCandidatesFunnel",
            })
            .get<JobCandidatesFunnelResponse>();
        return result;
    },

    loadJobEngagementFunnel: async (
        jobId: number
    ): Promise<JobEngagementFunnelResponse> => {
        const url = `/jobs/${jobId}/engagement/funnel`;
        const result = await Api.url(url)
            .throwOnAbort(true)
            .retries(3)
            .useSignal()
            .track({
                name: "loadJobEngagementFunnel",
            })
            .get<JobEngagementFunnelResponse>();
        return result;
    },

    loadJobStats: async function (jobId: number): Promise<JobStatsResponse> {
        const url = `/jobs/${jobId}/stats`;
        const result = await Api.url(url)
            .throwOnAbort(true)
            .retries(3)
            .useSignal()
            .track({
                name: "loadJobStats",
            })
            .get<JobStatsResponse>();
        return result;
    },
};
